import { useEffect, useRef, useState } from 'react';
import { ApiError, type Api } from '../api';
import type { T } from '../i18n';
import { PERM, useCan } from '../access';

/**
 * ADR-063: talk to the agent from the browser's microphone — no phone line. The microphone is turned into 20 ms
 * frames of 8 kHz 16-bit PCM (the phone's format) and sent over a WebSocket; the agent's voice comes back the same
 * way. The call itself runs exactly like a phone call (same agent, recording, scorecard). Test use: no minutes.
 */
const FRAME = 160;
// Downsampling in the audio thread: each 8 kHz sample is the average of the input samples it covers.
const WORKLET = `class Cav2Capture extends AudioWorkletProcessor {
  constructor() { super(); this.step = sampleRate / 8000; this.pos = 0; this.sum = 0; this.count = 0; this.buf = new Int16Array(${FRAME}); this.n = 0; }
  process(inputs) {
    const ch = inputs[0] && inputs[0][0];
    if (!ch) return true;
    for (let i = 0; i < ch.length; i++) {
      this.sum += ch[i]; this.count++; this.pos++;
      if (this.pos >= this.step) {
        this.pos -= this.step;
        const v = Math.max(-1, Math.min(1, this.sum / this.count)); this.sum = 0; this.count = 0;
        this.buf[this.n++] = v < 0 ? v * 0x8000 : v * 0x7fff;
        if (this.n === ${FRAME}) { this.port.postMessage(this.buf.buffer.slice(0)); this.n = 0; }
      }
    }
    return true;
  }
}
registerProcessor('cav2-capture', Cav2Capture);
// The agent's voice as ONE continuous stream (separate 20 ms buffers clicked at every seam — live 2026-10-01
// "cızırtılı"): 8 kHz samples are queued and read out at the device rate with linear interpolation; a short
// pre-buffer absorbs network jitter; an empty queue fades to silence instead of cutting.
class Cav2Playback extends AudioWorkletProcessor {
  constructor() {
    super();
    this.q = new Float32Array(8000 * 4); this.r = 0; this.w = 0; this.started = false;
    this.step = 8000 / sampleRate; this.frac = 0; this.last = 0;
    this.draining = false;
    this.port.onmessage = (e) => {
      if (e.data === 'drain') { this.draining = true; return; }
      const pcm = new Int16Array(e.data);
      for (let i = 0; i < pcm.length; i++) { this.q[this.w] = pcm[i] / 0x8000; this.w = (this.w + 1) % this.q.length; }
    };
  }
  size() { return (this.w - this.r + this.q.length) % this.q.length; }
  process(_inputs, outputs) {
    const out = outputs[0][0];
    if (!this.started && (this.size() >= 640 || (this.draining && this.size() > 0))) this.started = true;
    if (this.draining && this.size() < 2) { this.draining = false; this.port.postMessage('drained'); }
    for (let i = 0; i < out.length; i++) {
      if (!this.started || this.size() < 2) { this.last *= 0.95; out[i] = this.last; if (this.size() === 0) this.started = false; continue; }
      const a = this.q[this.r], b = this.q[(this.r + 1) % this.q.length];
      const v = a + (b - a) * this.frac;
      out[i] = v; this.last = v;
      this.frac += this.step;
      while (this.frac >= 1) { this.frac -= 1; this.r = (this.r + 1) % this.q.length; }
    }
    return true;
  }
}
registerProcessor('cav2-playback', Cav2Playback);`;

type Phase = 'idle' | 'connecting' | 'live' | 'ended' | 'error';

const MicIcon = ({ off }: { off?: boolean }) => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <rect x="9" y="3" width="6" height="11" rx="3" />
    <path d="M5 11a7 7 0 0 0 14 0M12 18v3" />
    {off && <path d="M4 4l16 16" />}
  </svg>
);
const HangUpIcon = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" aria-hidden>
    <path d="M12 9c-2.3 0-4.5.4-6.6 1.2-.8.3-1.3 1-1.3 1.9v2.3c0 .6.5 1.1 1.1 1.1.3 0 .6 0 .9-.1l2.4-.6c.5-.1.9-.6.9-1.1v-1.6c1.7-.5 3.5-.5 5.2 0v1.6c0 .5.4 1 .9 1.1l2.4.6c.3.1.6.1.9.1.6 0 1.1-.5 1.1-1.1v-2.3c0-.9-.5-1.6-1.3-1.9C16.5 9.4 14.3 9 12 9z" />
  </svg>
);

/** RMS of an analyser's current window (0–1). */
const levelOf = (a: AnalyserNode, buf: Float32Array<ArrayBuffer>): number => {
  a.getFloatTimeDomainData(buf);
  let sum = 0;
  for (const v of buf) sum += v * v;
  return Math.sqrt(sum / buf.length);
};

/** `inline`: the card sits in the page (test room) and is always shown; otherwise a button opens a floating card. */
export const WebCallButton = ({ api, t, agentId, agentName, onOpenCall, inline }: { api: Api; t: T; agentId: string; agentName: string; onOpenCall: (id: string) => void; inline?: boolean }) => {
  // ADR-071: a web call needs the Call permission. Without it nothing is offered — and the microphone is never
  // asked for (it is requested before the API would refuse).
  const can = useCan();
  const [phase, setPhase] = useState<Phase>('idle');
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string>();
  const [callId, setCallId] = useState<string>();
  const [seconds, setSeconds] = useState(0);
  const [muted, setMuted] = useState(false);
  const [speaker, setSpeaker] = useState<'agent' | 'user' | 'none'>('none');
  const stopRef = useRef<() => void>(() => undefined);
  const mutedRef = useRef(false);
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => () => stopRef.current(), []);
  useEffect(() => {
    if (phase !== 'live') return;
    const id = setInterval(() => setSeconds((x) => x + 1), 1000);
    return () => clearInterval(id);
  }, [phase]);

  const toggleMute = () => {
    mutedRef.current = !mutedRef.current;
    setMuted(mutedRef.current);
  };

  const start = async () => {
    setOpen(true);
    setError(undefined);
    setSeconds(0);
    setMuted(false);
    mutedRef.current = false;
    setSpeaker('none');
    setPhase('connecting');
    let stream: MediaStream | null = null;
    let ctx: AudioContext | null = null;
    let ws: WebSocket | null = null;
    let raf = 0;
    const stop = () => {
      cancelAnimationFrame(raf);
      try {
        if (ws?.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'hangup' }));
      } catch {
        /* closing anyway */
      }
      ws?.close();
      stream?.getTracks().forEach((tr) => tr.stop());
      void ctx?.close().catch(() => undefined);
    };
    stopRef.current = stop;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true, channelCount: 1 } });
      ctx = new AudioContext();
      const url = URL.createObjectURL(new Blob([WORKLET], { type: 'application/javascript' }));
      await ctx.audioWorklet.addModule(url);
      URL.revokeObjectURL(url);
      const r = await api.startWebCall(agentId);
      setCallId(r.callId);
      ws = new WebSocket(`${location.origin.replace(/^http/u, 'ws')}${r.path}?ticket=${encodeURIComponent(r.ticket)}`);
      ws.binaryType = 'arraybuffer';
      const mic = ctx.createMediaStreamSource(stream);
      const capture = new AudioWorkletNode(ctx, 'cav2-capture');
      mic.connect(capture);
      const silence = new ArrayBuffer(FRAME * 2);
      capture.port.onmessage = (e: MessageEvent<ArrayBuffer>) => {
        // Muted: the line keeps its rhythm with silence (the agent hears nothing).
        if (ws?.readyState === WebSocket.OPEN) ws.send(mutedRef.current ? silence : e.data);
      };
      // The agent's voice: one continuous stream through the playback worklet.
      const player = new AudioWorkletNode(ctx, 'cav2-playback', { numberOfInputs: 0, outputChannelCount: [1] });
      player.connect(ctx.destination);
      // Levels for the card: who is talking, how loud (drives the waves).
      const agentMeter = ctx.createAnalyser();
      const userMeter = ctx.createAnalyser();
      agentMeter.fftSize = userMeter.fftSize = 512;
      player.connect(agentMeter);
      mic.connect(userMeter);
      const buf = new Float32Array(512);
      let last: 'agent' | 'user' | 'none' = 'none';
      const tick = () => {
        const a = levelOf(agentMeter, buf);
        const u = mutedRef.current ? 0 : levelOf(userMeter, buf);
        const el = cardRef.current;
        if (el) {
          el.style.setProperty('--agent', Math.min(1, a * 6).toFixed(3));
          el.style.setProperty('--user', Math.min(1, u * 8).toFixed(3));
        }
        const now = a > 0.02 ? 'agent' : u > 0.03 ? 'user' : 'none';
        if (now !== last) setSpeaker((last = now));
        raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
      ws.onmessage = (e) => {
        if (typeof e.data === 'string') {
          if (e.data.includes('"ended"')) setPhase('ended');
          return;
        }
        player.port.postMessage(e.data);
      };
      ws.onopen = () => setPhase('live');
      // The line closed (the agent hung up, or the network): the agent's last words still queued here are played
      // to the end before the audio stops (2026-10-01: "iyi günler" was cut).
      let drained: () => void = () => undefined;
      player.port.onmessage = (e) => {
        if (e.data === 'drained') drained();
      };
      ws.onclose = async () => {
        await Promise.race([new Promise<void>((res) => { drained = res; player.port.postMessage('drain'); }), new Promise((res) => setTimeout(res, 4000))]);
        stop();
        setSpeaker('none');
        setPhase((p) => (p === 'connecting' ? 'error' : 'ended'));
      };
      ws.onerror = () => setError('WEB_CALL_CONNECTION');
    } catch (e) {
      stop();
      setError(e instanceof ApiError ? e.code : e instanceof DOMException && e.name === 'NotAllowedError' ? 'MIC_DENIED' : 'generic');
      setPhase('error');
    }
  };

  const mmss = `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
  const errorText = error
    ? t(`webcall.error.${error}`) !== `webcall.error.${error}`
      ? t(`webcall.error.${error}`)
      : t(`error.${error}`) !== `error.${error}`
        ? t(`error.${error}`)
        : t('error.generic')
    : '';
  const status =
    phase === 'connecting' ? t('webcall.connecting')
      : phase === 'live' ? (muted ? t('webcall.status.muted') : speaker === 'agent' ? t('webcall.status.agent') : speaker === 'user' ? t('webcall.status.user') : t('webcall.status.listening'))
        : phase === 'ended' ? t('webcall.status.ended')
          : phase === 'error' ? errorText : '';
  const live = phase === 'live' || phase === 'connecting';

  if (!can(PERM.call)) return null;
  return (
    <>
      {!inline && <button type="button" className="secondary" disabled={live} onClick={() => void start()}>🎙 {t('webcall.start')}</button>}
      {(open || inline) && (
        <div ref={cardRef} className={`wc-card${inline ? ' inline' : ''} ${phase} ${speaker}${muted ? ' is-muted' : ''}`} role="dialog" aria-label={t('webcall.start')}>
          <div className="wc-stage" aria-hidden>
            <div className="wc-waves left"><i /><i /><i /><i /></div>
            <div className="wc-orb">
              <span className="wc-orb-mic"><MicIcon off={muted} /></span>
              <span className="wc-orb-belt" />
            </div>
            <div className="wc-waves right"><i /><i /><i /><i /></div>
            <div className="wc-ripples"><i /><i /><i /></div>
          </div>
          <div className="wc-info">
            <strong>{agentName}</strong>
            <span className="wc-status" aria-live="polite">{status}</span>
            {phase === 'live' && <span className="wc-timer">{mmss}</span>}
          </div>
          {live ? (
            <div className="wc-actions">
              <button type="button" className={`wc-btn${muted ? ' on' : ''}`} disabled={phase !== 'live'} onClick={toggleMute} aria-pressed={muted} title={muted ? t('webcall.unmute') : t('webcall.mute')}>
                <MicIcon off={muted} />
                <span>{muted ? t('webcall.unmute') : t('webcall.mute')}</span>
              </button>
              <button type="button" className="wc-btn hangup" onClick={() => stopRef.current()} title={t('webcall.hangup')}>
                <HangUpIcon />
                <span>{t('webcall.hangup')}</span>
              </button>
            </div>
          ) : phase === 'idle' ? (
            <div className="wc-actions">
              <button type="button" className="primary" disabled={!agentId} onClick={() => void start()}>🎙 {t('webcall.begin')}</button>
            </div>
          ) : (
            <div className="wc-actions">
              {callId && phase === 'ended' && <button type="button" className="secondary" onClick={() => onOpenCall(callId)}>{t('call.openCall')}</button>}
              <button type="button" className="primary" onClick={() => void start()}>{t('webcall.again')}</button>
              {!inline && <button type="button" className="ghost" onClick={() => setOpen(false)}>{t('webcall.close')}</button>}
            </div>
          )}
          <p className="wc-hint">{t('webcall.hint')}</p>
        </div>
      )}
    </>
  );
};
